import React, { useMemo } from 'react';
import { Users, MessageSquare, Newspaper, Hash, TrendingUp, Star, Globe, Flag } from 'lucide-react';
import { generateUsers } from '../utils/mockData';
import { cn } from '../lib/utils';
import { resolveAvatar } from '../constants/sampleImages';

export const CommunityPage = () => {
  const users = useMemo(() => generateUsers(8), []);

  const groups = [
    { name: 'Sapphire Collectors', members: '12.4k', activity: 'High', color: 'bg-primary' },
    { name: 'Ratnapura Mining Hub', members: '4.2k', activity: 'Medium', color: 'bg-emerald-600' },
    { name: 'Gemology Enthusiasts', members: '8.9k', activity: 'High', color: 'bg-amber-500' },
    { name: 'Wholesale Sri Lanka', members: '2.1k', activity: 'Real-time', color: 'bg-blue-600' },
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
      {/* Left Sidebar - Groups & Stats */}
      <div className="lg:col-span-4 space-y-6">
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
          <h2 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-2">
            <TrendingUp size={20} className="text-primary" />
            Trending Groups
          </h2>
          <div className="space-y-4">
            {groups.map((group) => (
              <div key={group.name} className="flex items-center gap-4 group cursor-pointer">
                <div className={cn("w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold", group.color)}>
                  {group.name[0]}
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-bold text-slate-900 group-hover:text-primary transition-colors">{group.name}</h3>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">{group.members} Members</span>
                    <span className="text-[10px] bg-slate-50 text-slate-400 px-1.5 py-0.5 rounded border border-slate-100">Live</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <button className="w-full mt-6 py-3 text-sm font-bold text-slate-600 hover:bg-slate-50 rounded-xl transition-all border border-slate-200">
            Explore All Groups
          </button>
        </div>

        <div className="bg-primary p-6 rounded-2xl shadow-lg shadow-primary/20 text-white relative overflow-hidden group">
          <Globe className="absolute -right-4 -bottom-4 opacity-10 rotate-12 group-hover:scale-110 transition-transform" size={120} />
          <h3 className="text-lg font-bold mb-2">Build Your Expert Network</h3>
          <p className="text-sm text-primary-foreground/80 mb-6 leading-relaxed">
            Connect with verified miners and lapidaries directly to get the best industry insights.
          </p>
          <button className="bg-white text-primary font-bold py-2.5 px-6 rounded-xl text-xs hover:bg-slate-50 transition-colors shadow-sm">
            Invite Contacts
          </button>
        </div>
      </div>

      {/* Main Content Area - Discussions & Activity */}
      <div className="lg:col-span-8 space-y-6">
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden flex flex-col max-h-[calc(100vh-12rem)]">
          <div className="p-6 border-b border-slate-50 shrink-0">
            <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
              <MessageSquare size={24} className="text-primary" />
              Community Discussion
            </h2>
          </div>
          
          <div className="divide-y divide-slate-50 overflow-y-auto min-h-0 flex-1 custom-scrollbar">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="p-6 hover:bg-slate-50/50 transition-colors cursor-pointer group">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-slate-100 overflow-hidden shrink-0 border-2 border-white shadow-sm">
                    <img src={resolveAvatar(users[i]?.photoURL, i)} alt="Avatar" className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-bold text-slate-900 text-sm">{users[i]?.displayName}</span>
                      <span className="text-[10px] text-slate-400 font-medium">• 2h ago in</span>
                      <span className="text-[10px] font-bold text-primary bg-primary/5 px-1.5 py-0.5 rounded uppercase tracking-wider">Market Analysis</span>
                    </div>
                    <h3 className="font-bold text-slate-900 group-hover:text-primary transition-colors mb-2 leading-snug">
                      Is the current surge in Padparadscha Sapphire demand sustainable for the next quarter?
                    </h3>
                    <p className="text-sm text-slate-500 line-clamp-2 leading-relaxed mb-4">
                      I've been monitoring the auction yields from Geneva. The saturation levels requested by high-end retailers are becoming more specific. This could signal a shift in supply chain priorities for Sri Lankan miners...
                    </p>
                    <div className="flex items-center gap-6">
                      <span className="text-xs font-bold text-slate-400 flex items-center gap-1.5">
                        <MessageSquare size={14} /> 42 Replies
                      </span>
                      <span className="text-xs font-bold text-slate-400 flex items-center gap-1.5">
                        <Star size={14} /> 128 Upvotes
                      </span>
                      <button className="ml-auto text-[10px] font-bold text-slate-900 uppercase tracking-widest border border-slate-200 px-3 py-1 rounded-lg hover:border-primary transition-colors">
                        Join Debates
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
